// Who is signed in, asked once, at the top.
//
// # Two questions, two endpoints, and they are not the same question
//
// `/auth/session` is the BFF's: does this browser hold a live session, and whose.
// `/v1/session` is riksu's: what does the *token* say — the tenant, the scopes, the
// roles, and the `person` this principal is linked to, which is `null` whenever the
// authenticated principal is nobody's employment record.
//
// The app needs both and they can disagree in a way worth handling: a session can be
// live while its token has expired, and a principal can be perfectly authenticated
// and linked to no employee at all. Neither is an error.
//
// # No token reaches this module, ever
//
// Nothing here reads a credential, because there is nothing to read: the BFF holds
// it (ADR-0032). That is why this is a plain fetch and not an auth library.

import { ApiError, call, type components } from "../lib/api";

/** What riksu says about the caller. */
export type RiksuSession = components["schemas"]["Session"];

/** What the BFF says about the browser. */
export interface BffSession {
  readonly subject: string;
  readonly tenant_id: string;
  readonly expires_at: string;
  readonly candidate?: boolean;
}

export type SignInState =
  | { readonly status: "loading" }
  /** No live session. The app shows a way in and nothing else. */
  | { readonly status: "signed_out" }
  | {
      readonly status: "signed_in";
      readonly browser: BffSession;
      readonly riksu: RiksuSession;
    }
  | { readonly status: "candidate"; readonly browser: BffSession }
  /** A session exists and riksu refused it, or riksu is unreachable. */
  | { readonly status: "failed"; readonly failure: unknown };

/**
 * Resolves the sign-in state.
 *
 * The order matters. Asking the BFF first means a signed-out visitor never sends a
 * request riksu would answer with a 401 — which keeps "not signed in yet" out of
 * riksu's logs, where it reads like a rejected caller rather than a person who has
 * not started.
 */
export async function resolveSignIn(): Promise<SignInState> {
  let browser: BffSession;
  try {
    browser = await call<BffSession>("/auth/session");
  } catch (failure) {
    if (failure instanceof ApiError && failure.status === 401) {
      return { status: "signed_out" };
    }
    return { status: "failed", failure };
  }

  if (browser.candidate === true) return { status: "candidate", browser };

  try {
    const riksu = await call<RiksuSession>("/v1/session");
    return { status: "signed_in", browser, riksu };
  } catch (failure) {
    // A live cookie whose token riksu will not accept. Reported rather than
    // silently downgraded to "signed out": the two have different causes and only
    // one of them is fixed by signing in again.
    return { status: "failed", failure };
  }
}

/** Where to send somebody who needs to sign in, back to where they were. */
export function signInHref(returnTo: string): string {
  return `/auth/login?return_to=${encodeURIComponent(returnTo)}`;
}
